import { Avatar, Box, Dialog, DialogContent, Divider, Stack } from '@mui/material';
import { useHref } from 'react-router-dom';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { toTitleCase } from 'common/utils';
import { colors } from 'styles/colors';
import { EContentReportStatus, EContentReportTarget } from 'enum/EContentReport';

interface Props {
  report: any | null;
  resolving: boolean;
  onResolve: (reportId: string) => void;
  onClose: () => void;
}

// One report in full - the note especially, which a table cell cuts short.
export default function ContentReportModal({ report, resolving, onResolve, onClose }: Props) {
  const productHref = useHref(`/products/all-products/${report?.targetId ?? ''}`);
  if (!report) return null;

  const isProduct = report.targetType === EContentReportTarget.PRODUCT;
  const resolved = report.status === EContentReportStatus.RESOLVED;

  const row = (label: string, value: React.ReactNode) => (
    <Box display="flex" gap={2}>
      <Box width={110} flexShrink={0}>
        <CustomText text={label} fontSize={13} color={colors.gray} />
      </Box>
      <Box minWidth={0}>{typeof value === 'string' ? <CustomText text={value} fontSize={14} /> : value}</Box>
    </Box>
  );

  return (
    <Dialog open={!!report} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        {/* What it is about */}
        <Box display={'flex'} alignItems={'center'} gap={1.5}>
          {isProduct && (
            <Avatar src={report.targetAvatar ?? undefined} sx={{ width: 44, height: 44, borderRadius: '6px', bgcolor: colors.avatarBg }} />
          )}
          <Box minWidth={0}>
            <CustomText text={report.targetName ?? report.targetId} fontSize={17} fontWeight={700} color={colors.black} />
            <CustomText text={toTitleCase(report.targetType)} fontSize={13} color={colors.gray} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack gap={1.5}>
          {row('Section', toTitleCase(report.section))}
          {/* Which ingredient, on a product's ingredients. */}
          {report.detail ? row('Ingredient', report.detail) : null}
          {row('Reported by', report.user?.fullName ?? '-')}
          {row('Date', new Date(report.createdAt).toLocaleString())}
          {row('Status', resolved ? `Resolved${report.resolvedAt ? ` on ${new Date(report.resolvedAt).toLocaleString()}` : ''}` : 'Open')}
        </Stack>

        <Box mt={2.5}>
          <CustomText text="Note" fontSize={13} color={colors.gray} />
          <Box mt={0.75} p={1.5} sx={{ bgcolor: colors.topBackground, borderRadius: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <CustomText text={report.note ?? '-'} fontSize={15} />
          </Box>
        </Box>

        <Box mt={3} display="flex" gap={1} justifyContent="flex-end">
          <CustomButton text="Close" variant="outlined" height={40} onClick={onClose} />
          {isProduct && (
            <CustomButton
              text="Open product"
              variant="outlined"
              height={40}
              onClick={() => window.open(productHref, '_blank', 'noopener,noreferrer')}
            />
          )}
          {!resolved && (
            <CustomButton text="Resolve" height={40} isLoading={resolving} disabled={resolving} onClick={() => onResolve(report.id)} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
